/* eslint-disable react-refresh/only-export-components */
"use client";

import React, {createContext, ReactNode, useContext} from 'react';
import useSWR from 'swr';

export interface AgentIntegrationsConfig {
    xero?: {
        tenants: {
            "tenantId": string;
            "tenantType": string;
            "tenantName": string;
            "createdDateUtc": string;
            "updatedDateUtc": string;
        }[]
    }
}

export interface Agent {
    slug: string;
    name: string;
    welcomeMessage: string;
    public: boolean;
    toolMode?: "legacy" | "vnext" | string;
    blockedTools?: string[];
    primaryModel: {
        providerName: string;
        modelName: string;
        modelId: string;
    }
    integrations: AgentIntegrationsConfig;
}

interface AgentContextType {
    agents: Agent[];
    loading: boolean;
    error: string | null;
    getAgent: (slug: string) => Agent | undefined;
    refreshAgents: () => void;
}

export const AgentContext = createContext<AgentContextType | undefined>(undefined);

export const useAgents = () => {
    const context = useContext(AgentContext);
    if (context === undefined) {
        throw new Error('useAgents must be used within an AgentProvider');
    }
    return context;
};

interface AgentProviderProps {
    children: ReactNode;
}

// SWR fetcher function
const fetcher = async (url: string): Promise<Agent[]> => {
    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch agents: ${response.status}`);
    }

    const data = await response.json();
    return data || [];
};

export const AgentProvider: React.FC<AgentProviderProps> = ({children}) => {
    const {data: agents = [], error, isLoading, mutate} = useSWR<Agent[]>(
        '/api/agents/',
        fetcher,
        {
            revalidateOnFocus: true,
            revalidateOnReconnect: true,
            dedupingInterval: 0,
            errorRetryCount: 3,
            refreshInterval: 30000, // Refresh every 30s
            onError: () => {
                // Error is captured in the error state
            }
        }
    );

    const getAgent = (slug: string): Agent | undefined => {
        return agents.find(agent => agent.slug === slug);
    };

    const refreshAgents = () => {
        mutate();
    };

    const value: AgentContextType = {
        agents,
        loading: isLoading,
        error: error ? (error instanceof Error ? error.message : 'Failed to fetch agents') : null,
        getAgent,
        refreshAgents,
    };

    return (
        <AgentContext.Provider value={value}>
            {children}
        </AgentContext.Provider>
    );
};
